import { createTransactionsFromCsv } from "@/apis/developer-tools";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { AlertCircle, FileText, Loader2, Upload, X } from "lucide-react";
import { useState } from "react";

type Props = {
  isDialogOpen: boolean;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
};

interface ParsedTransaction {
  amount: string;
  counterpartyName: string;
  counterpartyIban: string;
  purpose: string;
  currency: string;
  side: string;
  bookingDate: string;
  valueDate: string;
  row: number;
}

interface ValidationResult {
  validTransactions: ParsedTransaction[];
  totalRows: number;
  validRows: number;
  errors: string[];
}

const REQUIRED_COLUMNS = [
  "amount",
  "counterpartyName",
  "counterpartyIban",
  "purpose",
  "currency",
  "side",
  "bookingDate",
  "valueDate",
];

const OPTIONAL_COLUMNS: string[] = [];

export const ManuallyUploadTransactions = ({
  isDialogOpen,
  setIsDialogOpen,
}: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateCSV = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split("\n").filter((line) => line.trim());

          if (lines.length < 2) {
            reject(
              new Error("CSV must have at least a header row and one data row"),
            );
            return;
          }

          // Parse header
          const header = lines[0]
            .split(",")
            .map((col) => col.trim().replace(/"/g, ""));

          // Check required columns
          const missingColumns = REQUIRED_COLUMNS.filter(
            (col) => !header.includes(col),
          );
          if (missingColumns.length > 0) {
            reject(
              new Error(
                `Missing required columns: ${missingColumns.join(", ")}`,
              ),
            );
            return;
          }

          const validTransactions: ParsedTransaction[] = [];
          const errors: string[] = [];

          // Parse data rows
          for (let i = 1; i < lines.length; i++) {
            const rowData = lines[i]
              .split(",")
              .map((cell) => cell.trim().replace(/"/g, ""));

            if (rowData.length !== header.length) {
              errors.push(`Row ${i + 1}: Column count mismatch`);
              continue;
            }

            const transaction: any = { row: i + 1 };
            let hasError = false;

            // Map columns to transaction object
            header.forEach((col, index) => {
              if (
                REQUIRED_COLUMNS.includes(col) ||
                OPTIONAL_COLUMNS.includes(col)
              ) {
                transaction[col] = rowData[index];
              }
            });

            // Validate required fields are not empty
            for (const requiredCol of REQUIRED_COLUMNS) {
              if (
                !transaction[requiredCol] ||
                transaction[requiredCol].trim() === ""
              ) {
                errors.push(`Row ${i + 1}: Missing value for ${requiredCol}`);
                hasError = true;
              }
            }

            // Validate amount is numeric
            if (transaction.amount && isNaN(Number(transaction.amount))) {
              errors.push(`Row ${i + 1}: Amount must be numeric`);
              hasError = true;
            }

            // Validate side is 'debit' or 'credit'
            if (
              transaction.side &&
              !["debit", "credit"].includes(transaction.side.toLowerCase())
            ) {
              errors.push(`Row ${i + 1}: Side must be 'debit' or 'credit'`);
              hasError = true;
            }

            // Validate date formats (basic check)
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (
              transaction.bookingDate &&
              !dateRegex.test(transaction.bookingDate)
            ) {
              errors.push(
                `Row ${i + 1}: bookingDate must be in YYYY-MM-DD format`,
              );
              hasError = true;
            }
            if (
              transaction.valueDate &&
              !dateRegex.test(transaction.valueDate)
            ) {
              errors.push(
                `Row ${i + 1}: valueDate must be in YYYY-MM-DD format`,
              );
              hasError = true;
            }

            if (!hasError) {
              validTransactions.push(transaction);
            }
          }

          resolve({
            validTransactions,
            totalRows: lines.length - 1, // Exclude header
            validRows: validTransactions.length,
            errors,
          });
        } catch (error) {
          reject(new Error("Failed to parse CSV file"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };

  const handleFileUpload = async (selectedFile: File) => {
    setFile(selectedFile);
    setIsParsing(true);
    setError(null);
    setIsLoading(true);
    setValidationResult(null);

    try {
      const result = await validateCSV(selectedFile);
      setValidationResult(result);

      if (result.errors.length > 0) {
        console.warn("CSV validation warnings:", result.errors);
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to validate CSV",
      );
      setFile(null);
    } finally {
      setIsParsing(false);
      setIsLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setValidationResult(null);
    setError(null);

    // Reset file input
    const fileInput = document.getElementById(
      "transaction-csv-upload",
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleConfirmAction = async () => {
    try {
      setIsLoading(true);
      if (!file) {
        throw new Error("No file selected");
      }
      await createTransactionsFromCsv(file);
      setIsDialogOpen(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setError(null);
      setFile(null);
      setValidationResult(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
      <DialogPortal container={document.body}>
        <DialogContent
          className="z-[9999] max-w-2xl" // Increased width for better CSV info display
          onPointerDownOutside={(e) => {
            e.preventDefault();
            handleDialogClose(false);
          }}
          onEscapeKeyDown={() => {
            handleDialogClose(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Upload Transaction CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file containing transaction data with the required
              format.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="default">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Required CSV Format</AlertTitle>
            <AlertDescription>
              <div className="mt-2">
                <p className="mb-2">
                  Required columns: amount, counterpartyName, counterpartyIban,
                  purpose, currency, side, bookingDate, valueDate
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  • Dates should be in YYYY-MM-DD format
                  <br />• Side should be 'debit' or 'credit'
                  <br />• Amount should be numeric
                </p>
              </div>
            </AlertDescription>
          </Alert>

          <Alert variant="warning">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Important Prerequisite</AlertTitle>
            <AlertDescription>
              Ensure the SMB has a bank account connected before proceeding.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Validation Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <Label>Transaction CSV File</Label>

            {!file ? (
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer",
                  dragActive ? "border-primary" : "border-gray-300",
                  "hover:border-primary transition-colors",
                )}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="transaction-csv-upload"
                />
                <label
                  htmlFor="transaction-csv-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="h-8 w-8 mb-2 text-gray-500" />
                  <span className="text-sm font-medium">
                    <strong>Click to upload or drag and drop</strong>
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    CSV files only
                  </span>
                </label>
              </div>
            ) : (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gray-100 p-2 rounded">
                      <FileText className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium truncate max-w-[200px]">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </span>
                      {isParsing && (
                        <div className="flex items-center space-x-2 mt-1">
                          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                          <span className="text-xs text-blue-500">
                            Validating CSV format...
                          </span>
                        </div>
                      )}
                      {validationResult && !isParsing && (
                        <div className="flex items-center space-x-2 mt-1">
                          <span
                            className={cn(
                              "text-xs font-medium",
                              validationResult.validRows ===
                                validationResult.totalRows
                                ? "text-green-600"
                                : validationResult.validRows > 0
                                  ? "text-yellow-600"
                                  : "text-red-600",
                            )}
                          >
                            {validationResult.validRows} of{" "}
                            {validationResult.totalRows} valid transactions
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="p-1 rounded-full hover:bg-gray-100"
                    title="Remove file"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            )}

            {/* Validation warnings */}
            {validationResult && validationResult.errors.length > 0 && (
              <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-yellow-800 mb-1">
                      Validation Issues Found
                    </h4>
                    <div className="text-sm text-yellow-700 max-h-32 overflow-y-auto">
                      {validationResult.errors
                        .slice(0, 10)
                        .map((error, index) => (
                          <div key={index} className="mb-1">
                            • {error}
                          </div>
                        ))}
                      {validationResult.errors.length > 10 && (
                        <div className="text-xs mt-2 italic">
                          ... and {validationResult.errors.length - 10} more
                          issues
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogClose(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={
                isLoading ||
                !validationResult ||
                validationResult.validRows === 0
              }
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Upload {validationResult?.validRows || 0} Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
